import { BlockTitle } from "@blocks/Blocks";
import { useWindowSize } from "@hooks/useWindowSize";
import { getCardLayout } from "@pages/Global/utils/helpers";
import useOKTAUserStore, { OKTAUserTypeEnum } from "@store/useOKTAUserStore";
import { SanitizedSectionArticle } from "@typings/Cue";
import { sectionNavigationItems } from "@util/constant";
import { cn } from "@util/helpers";

type CompaniesSectionProps = {
  /** The articles to be displayed in the section */
  articles: SanitizedSectionArticle[];
  /** Optional className for the section */
  className?: string;
  /** Optional link params */
  linkParams?: URLSearchParams;
};

/**
 * CompaniesSection component to display a list of articles related to /companies-markets and /keywords/asia-pacific.
 * It renders a section with a title and a list of articles as cards.
 */
const CompaniesSection = ({
  articles,
  className,
  linkParams,
}: CompaniesSectionProps): React.ReactElement => {
  const OKTAUserInfo = useOKTAUserStore((state) => state.userInfo);
  const { isScreenLG } = useWindowSize();

  const sectionData = {
    ...sectionNavigationItems["companies-markets"],
    label: "Companies",
  };

  if (!articles.length) return <></>;

  return (
    <section
      data-testid="global-companies-section-component"
      className={className}
    >
      <BlockTitle
        link={sectionData.link}
        text={sectionData.label.toUpperCase()}
      />

      <div
        className={cn(
          "relative mb-6 grid w-full grid-cols-1 lg:grid-cols-3 lg:divide-x"
        )}
      >
        {articles.map((article, index) => {
          const CardLayout = getCardLayout(index);

          return (
            <CardLayout
              key={index}
              article={article}
              displayBlurb={index === 0}
              displayEdited={!isScreenLG && index === 0}
              displayUpdated={index === 0 || !isScreenLG}
              displaySection
              contentClassName={cn("px-0 lg:px-3", {
                "lg:pr-3 lg:pl-0": index % 2 !== 0,
              })}
              className={cn(
                "relative my-4 block lg:my-6",
                "after:absolute after:-bottom-4 after:w-full after:border-b after:border-gray-175 after:lg:-bottom-6",
                { "mt-4": index === 0 || index === 1 || index === 2 },
                {
                  "after:lg:hidden":
                    index === 0 || index === articles.length - 2,
                },
                { "after:hidden": index === articles.length - 1 },
                { "lg:mr-3 lg:border-r lg:pr-3": index === 0 },
                { "border-none": index % 2 !== 0 },
                { "row-span-4": index === 0 }
              )}
              linkParams={linkParams}
              displayImage={index === 0}
              disablePaywall={
                OKTAUserInfo?.usertype !== OKTAUserTypeEnum.SUBSCRIBER
              }
            />
          );
        })}
      </div>
    </section>
  );
};

export default CompaniesSection;
